import {
  clearSession,
  getSession,
  updateSession,
} from '@/shared/store/session';

import { RefreshToken } from '@/services/auth';

// Grace period: 2 minutes after login before checking expiry
const GRACE_PERIOD_MS = 2 * 60 * 1000;
// Buffer time: 1 minute before expiry to refresh proactively
const EXPIRY_BUFFER_MS = 60 * 1000;

// Singleton pattern: prevent concurrent refresh attempts
let refreshPromise = null;

// Callback system for session events
const callbacks = {
  refresh: [],
  expired: [],
};

/**
 * Register callback for session refresh events
 * @param {Function} callback - Callback function
 */
export const onSessionRefresh = (callback) => {
  if (typeof callback === 'function') {
    callbacks.refresh.push(callback);
  }
};

/**
 * Register callback for session expiration events
 * @param {Function} callback - Callback function
 */
export const onSessionExpired = (callback) => {
  if (typeof callback === 'function') {
    callbacks.expired.push(callback);
  }
};

/**
 * Remove callback from refresh events
 * @param {Function} callback - Callback function to remove
 */
export const offSessionRefresh = (callback) => {
  callbacks.refresh = callbacks.refresh.filter((cb) => cb !== callback);
};

/**
 * Remove callback from expiration events
 * @param {Function} callback - Callback function to remove
 */
export const offSessionExpired = (callback) => {
  callbacks.expired = callbacks.expired.filter((cb) => cb !== callback);
};

/**
 * Trigger refresh callbacks
 * @param {Object} session - Updated session object
 */
const triggerRefreshCallbacks = (session) => {
  callbacks.refresh.forEach((callback) => {
    try {
      callback(session);
    } catch (error) {
      console.error('Error in refresh callback:', error);
    }
  });
};

/**
 * Trigger expiration callbacks
 */
const triggerExpiredCallbacks = () => {
  callbacks.expired.forEach((callback) => {
    try {
      callback();
    } catch (error) {
      console.error('Error in expired callback:', error);
    }
  });
};

/**
 * Check if session is expired or expiring soon
 * @param {Object} session - Session object
 * @returns {boolean} True if expired or expiring
 */
export const isSessionExpiredOrExpiring = (session) => {
  if (!session || !session.expire) {
    return true;
  }

  const now = Date.now();
  const loginTime = session.loginTime || now;
  const timeSinceLogin = now - loginTime;

  // Grace period: don't check expiry within 2 minutes of login
  if (timeSinceLogin < GRACE_PERIOD_MS) {
    return false;
  }

  // Check if token is expired or expiring within buffer time
  return now >= session.expire - EXPIRY_BUFFER_MS;
};

/**
 * Refresh tokens using refresh token
 * @returns {Promise<Object>} New token data
 */
export const refreshTokens = async () => {
  // If refresh is already in progress, return the existing promise
  if (refreshPromise) {
    return refreshPromise;
  }

  // Create refresh promise
  refreshPromise = (async () => {
    try {
      const session = getSession();
      if (!session || !session.refresh_token) {
        throw new Error('No session or refresh token available');
      }

      const response = await RefreshToken(session.refresh_token);
      const newTokenData = response.data;

      // Update session with new tokens
      const updatedSession = updateSession(newTokenData, session);

      // Trigger refresh callbacks
      triggerRefreshCallbacks(updatedSession);

      return updatedSession;
    } catch (error) {
      // On error, clear session and trigger expired callbacks
      clearSession();
      triggerExpiredCallbacks();
      throw error;
    } finally {
      // Clear the promise so next refresh can proceed
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

/**
 * Ensure token is valid, refresh if needed
 * @returns {Promise<Object>} Valid session object
 */
export const ensureValidToken = async () => {
  const session = getSession();
  if (!session) {
    throw new Error('No session available');
  }

  // Check if token needs refresh
  if (isSessionExpiredOrExpiring(session)) {
    return await refreshTokens();
  }

  return session;
};
